import React, { useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface AtmosphericDistortionProps {
  width?: number;
  height?: number;
}

export const AtmosphericDistortion: React.FC<AtmosphericDistortionProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const midX = size / 2;
    const atmoTop = size * 0.4;
    const atmoBottom = size * 0.7;

    // Draw Atmosphere Band
    const atmoGrad = ctx.createLinearGradient(0, atmoTop, 0, atmoBottom);
    atmoGrad.addColorStop(0, 'rgba(56, 189, 248, 0)');
    atmoGrad.addColorStop(0.5, 'rgba(56, 189, 248, 0.2)');
    atmoGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
    ctx.fillStyle = atmoGrad;
    ctx.fillRect(0, atmoTop, size, atmoBottom - atmoTop);

    // Draw Star
    ctx.save();
    const starGrad = ctx.createRadialGradient(midX, 30, 2, midX, 30, 20);
    starGrad.addColorStop(0, '#FFFFFF');
    starGrad.addColorStop(0.5, '#FDE047');
    starGrad.addColorStop(1, 'rgba(253, 224, 71, 0)');
    ctx.fillStyle = starGrad;
    ctx.beginPath();
    ctx.arc(midX, 30, 20, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Draw Ray
    ctx.strokeStyle = '#FDE047';
    ctx.lineWidth = 2;
    ctx.beginPath();
    
    // Straight ray from star to atmosphere
    ctx.moveTo(midX, 40);
    ctx.lineTo(midX, atmoTop);

    // Wobbly ray inside and below atmosphere
    let currentX = midX;
    for (let y = atmoTop; y <= size - 40; y += 5) {
      // Wiggle increases as it goes deeper
      const depth = (y - atmoTop) / (size - 40 - atmoTop);
      const wiggleAmt = depth * 15;
      
      // Use sine waves + time for pseudo-random wiggle
      const offset = Math.sin(y * 0.1 + frameCount * 0.2) * Math.cos(y * 0.05 - frameCount * 0.1) * wiggleAmt;
      
      currentX = midX + offset;
      ctx.lineTo(currentX, y);
    }
    ctx.stroke();

    // Draw Eye at bottom
    const eyeY = size - 30;
    ctx.fillStyle = '#94A3B8';
    ctx.beginPath();
    ctx.arc(midX, eyeY, 15, Math.PI * 1.2, Math.PI * 2.8);
    ctx.lineTo(midX - 15, eyeY);
    ctx.fill();
    ctx.beginPath();
    ctx.fillStyle = '#0F172A';
    ctx.arc(midX + 3, eyeY, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#60A5FA';
    ctx.beginPath();
    ctx.arc(midX + 3, eyeY, 3, 0, Math.PI * 2);
    ctx.fill();

    // Labels
    ctx.fillStyle = '#E2E8F0';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Star — steady light', midX + 25, 35);
    
    ctx.fillStyle = '#38BDF8';
    ctx.textAlign = 'center';
    ctx.fillText('Turbulent Air Layers', midX, atmoTop + 20);

    ctx.fillStyle = '#F8FAFC';
    ctx.textAlign = 'center';
    ctx.fillText('We see a twinkling star', midX, size - 10);

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};
